import { useState, useEffect, useRef, useCallback } from 'react';
import { eventApi } from '../services/api';
import { addPendingEvent, getAllPendingEvents, removePendingEvent } from '../crypto';

export interface PendingEvent {
  id: string;
  babyId: string;
  type: string;
  timestamp: string;
  encryptedData: string;
  encryptedNotes?: string;
  createdAt: number;
}

export function useOfflineSync() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const syncingRef = useRef(false);

  // Load initial pending count
  useEffect(() => {
    getAllPendingEvents().then((events) => setPendingCount(events.length));
  }, []);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncNow();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const queueEvent = useCallback(async (payload: Omit<PendingEvent, 'id' | 'createdAt'>) => {
    const event: PendingEvent = {
      ...payload,
      id: crypto.randomUUID(),
      createdAt: Date.now(),
    };
    await addPendingEvent(event);
    setPendingCount((c) => c + 1);
  }, []);

  const syncNow = useCallback(async () => {
    if (syncingRef.current) return;
    syncingRef.current = true;

    try {
      const events = await getAllPendingEvents();
      for (const event of events) {
        try {
          await eventApi.create(event.babyId, {
            type: event.type,
            timestamp: event.timestamp,
            encryptedData: event.encryptedData,
            encryptedNotes: event.encryptedNotes,
          });
          await removePendingEvent(event.id);
          setPendingCount((c) => Math.max(0, c - 1));
        } catch (err) {
          console.error('Failed to sync pending event:', err);
          // Leave in queue, continue to next
        }
      }
    } finally {
      syncingRef.current = false;
    }
  }, []);

  // Attempt sync when coming back online (also on mount if online with pending)
  useEffect(() => {
    if (isOnline && pendingCount > 0) {
      syncNow();
    }
  }, [isOnline, pendingCount, syncNow]);

  return { isOnline, pendingCount, queueEvent, syncNow };
}
